/**
 * The right panel — one aligned table where every row is "some book, per
 * selection".
 *
 * Hand-rolled `<table>` rather than `DataTable`, and deliberately so: this table
 * needs row GROUPS, per-row EXPANDERS and INDENTATION, and DataTable has none of
 * the three. Bending it to have them would cost more than the table it saves,
 * so please do not "fix" this back into a DataTable.
 *
 * Row order is deliberately NOT the recording's. The original puts "Total (X)"
 * and the two Betfair rows ABOVE the real book. Inverted here: a simulated
 * figure never sits above a real one in reading order, because reading order is
 * what an operator scanning under time pressure actually trusts.
 */

import { useCallback, useEffect, useState } from 'react'
import { Avatar, Button, Card, Chip, RoleChip, ScrollRegion, Skeleton } from '@w7/ui'
import { Money, exportCsv } from '@/app/bits'
import { isoDay, money, odds } from '@/lib/format'
import { round2 } from '@/lib/book'
import type { MarketBook } from '@/data/api'
import { ROLE_LABEL, type BookBranchRow, type BookPosition } from '@/data/types'
import { BookBranch, BookCells, RootExpander } from './BookTreeRows'
import { HedgeBox, samePositions, type HedgeCommit } from './HedgeBox'

export function BookPanel({
  marketId,
  eventName,
  book,
  basis,
  loading,
  onSwitchBasis,
}: {
  marketId: string
  eventName: string
  book: MarketBook | null
  basis: 'my' | 'total'
  loading: boolean
  onSwitchBasis: () => void
}) {
  const market = book?.market ?? null
  const positions: BookPosition[] = market?.selections ?? []
  const selectionCount = positions.length
  const span = selectionCount + 2

  /* ------------------------------------------------------------- the tree -- */

  const rootId = book?.root.id ?? ''
  // Root expanded by default: the first question is always "which of my four
  // super masters is this?". `null` means "nobody has touched it yet", so the
  // default holds without an effect that would fight a deliberate collapse.
  const [expanded, setExpanded] = useState<Set<string> | null>(null)
  const openRows = expanded ?? new Set(rootId ? [rootId] : [])
  const toggle = useCallback(
    (accountId: string) =>
      setExpanded((current) => {
        // Materialise the default before applying the toggle: without this the
        // first click on the root's twisty would OPEN a row that is already
        // open on screen.
        const next = new Set(current ?? (rootId ? [rootId] : []))
        if (next.has(accountId)) next.delete(accountId)
        else next.add(accountId)
        return next
      }),
    [rootId],
  )

  // What each expanded branch actually returned, so Export book writes exactly
  // the rows on screen and never a level the operator has not opened.
  const [branches, setBranches] = useState<Record<string, BookBranchRow[]>>({})
  const onLoaded = useCallback((parentId: string, rows: BookBranchRow[] | null) => {
    setBranches((current) => {
      if (rows === null) {
        if (!(parentId in current)) return current
        const next = { ...current }
        delete next[parentId]
        return next
      }
      if (current[parentId] === rows) return current
      return { ...current, [parentId]: rows }
    })
  }, [])

  /* ------------------------------------------------------------ the hedge -- */

  const [commit, setCommit] = useState<HedgeCommit | null>(null)
  // A hedge computed under one basis is meaningless under the other, and one
  // computed on another market is nonsense — drop it rather than carry it.
  useEffect(() => setCommit(null), [marketId, basis])
  const moved = commit ? !samePositions(commit.positions, positions) : false

  /* ---------------------------------------------------------------- csv --- */

  const exportBook = () => {
    if (!book || !market) return
    const rows: Record<string, unknown>[] = []

    const push = (
      label: string,
      role: string,
      depth: number,
      entry: {
        positions: BookPosition[]
        worstCase: number
        bestCase: number
        betCount: number
        memberCount: number
      },
    ) => {
      const cells: Record<string, unknown> = {}
      for (const position of entry.positions) cells[`net_${position.name}`] = position.net
      rows.push({
        // Every exported row names where it came from. This file leaves the
        // building; the three simulated rows deliberately never do.
        source: 'ledger projection',
        basis,
        event: eventName,
        market: market.marketName,
        row: label,
        role,
        depth,
        worst_case: entry.worstCase,
        best_case: entry.bestCase,
        bets: entry.betCount,
        members: entry.memberCount,
        ...cells,
      })
    }

    push(book.root.username, ROLE_LABEL[book.root.role] ?? book.root.role, 0, {
      positions,
      worstCase: market.worstCase,
      bestCase: market.bestCase,
      betCount: market.betCount,
      memberCount: market.memberCount,
    })

    const walk = (parentId: string, depth: number) => {
      for (const row of branches[parentId] ?? []) {
        push(row.account.username, ROLE_LABEL[row.account.role] ?? row.account.role, depth, row)
        if (openRows.has(row.account.id)) walk(row.account.id, depth + 1)
      }
    }
    if (openRows.has(rootId)) walk(rootId, 1)

    exportCsv(`market-book-${marketId}-${isoDay(Date.now())}.csv`, rows)
  }

  /* --------------------------------------------------------------- render -- */

  return (
    <Card
      title="The book"
      // The export caveat is in the always-visible subtitle rather than a
      // tooltip on the button: the operator has to know what is NOT in the file
      // before they press it, not after. It is repeated on the button's `title`
      // so it is also there at the moment of the click.
      subtitle="Every row is one account's book, per selection. Green is what the book keeps if that outcome wins; red is what it pays out. Simulated exchange rows are not exported."
      actions={
        <Button
          variant="ghost"
          size="sm"
          icon="download"
          onClick={exportBook}
          disabled={loading || !book}
          title="Exports the real rows only — the root and every branch you have expanded. The simulated exchange rows are never written."
        >
          Export book
        </Button>
      }
      flush
    >
      <div style={{ padding: 'var(--w7-s4) var(--w7-s4) 0' }}>
        <HedgeBox
          positions={positions}
          prices={book?.prices ?? []}
          matched={market?.matched ?? 0}
          basis={basis}
          commit={commit}
          moved={moved}
          onCommit={setCommit}
          onSwitchBasis={onSwitchBasis}
        />
      </div>

      <ScrollRegion
        label={`Book by selection for ${market?.marketName ?? 'this market'}`}
      >
        <table className="w7-book">
          <caption className="w7-sr">
            Book by selection — {eventName}, {market?.marketName ?? 'market'}
          </caption>
          <thead>
            <tr>
              <th scope="col" className="w7-book-label">
                Row
              </th>
              <th
                scope="col"
                className="w7-book-net"
                title="The worst outcome for that row — the smallest number across the selections. Never added down this column: two rows' worst cases sit on different outcomes."
              >
                Net exposure
              </th>
              {positions.map((position) => (
                <th scope="col" key={position.name}>
                  {position.name}
                </th>
              ))}
            </tr>
            {/* Children's per-selection books SUM to their parent's, because the
                book is additive over legs and the subtrees partition the
                members. Worst case does NOT, because min(Σ) ≠ Σ(min) — which is
                why that column carries no total anywhere on this table. If a
                reviewer wants one, the parent row is already on screen. */}
            <tr className="w7-mb-subhead">
              <td className="w7-book-label" />
              <td className="w7-book-net">never totalled</td>
              {selectionCount > 0 ? <td colSpan={selectionCount}>children add up</td> : null}
            </tr>
          </thead>

          <tbody>
            {loading || !book || !market ? (
              <BookSkeleton rows={4} selectionCount={Math.max(selectionCount, 3)} />
            ) : (
              <>
                {/* ------------------------------------------- group A -- */}
                <tr className="w7-book-group">
                  <td colSpan={span}>
                    Your book — the signed-in account, then the accounts beneath it.
                  </td>
                </tr>

                <tr className="w7-book-row" data-kind="root">
                  <th scope="row" className="w7-book-label">
                    <span className="w7-book-indent">
                      <RootExpander
                        expanded={openRows.has(rootId)}
                        onToggle={() => toggle(rootId)}
                        accountId={rootId}
                        childCount={book.root.childCount}
                        worstCase={market.worstCase}
                      />
                      <Avatar name={book.root.displayName || book.root.username} size="sm" />
                      <span className="w7-truncate">{book.root.username}</span>
                      <RoleChip
                        role={book.root.role}
                        label={ROLE_LABEL[book.root.role] ?? book.root.role}
                      />
                    </span>
                  </th>
                  {/* The root's cells ARE the market total — they are the same
                      figure, so a separate "Total book" row on top would be the
                      same number twice under two different names. */}
                  <BookCells positions={positions} worstCase={market.worstCase} stale={false} />
                </tr>

                {openRows.has(rootId) && rootId ? (
                  <BookBranch
                    marketId={marketId}
                    basis={basis}
                    parentId={rootId}
                    expectedChildren={book.root.childCount}
                    depth={1}
                    selectionCount={selectionCount}
                    parentName={book.root.username}
                    expanded={openRows}
                    onToggle={toggle}
                    onLoaded={onLoaded}
                  />
                ) : null}

                {market.betCount === 0 ? (
                  <tr className="w7-mb-rowinfo">
                    <td colSpan={span}>
                      Nothing beneath you holds an open position here. Everything on this market has
                      settled, or nobody has bet it.
                    </td>
                  </tr>
                ) : null}

                {/* ------------------------------------------- group B -- */}
                {commit ? (
                  <>
                    <tr className="w7-book-group">
                      <td colSpan={span}>
                        Hedge calculator — the book after backing {commit.selection} for{' '}
                        {money(commit.result.stake)} at {odds(commit.price)}. Nothing has been
                        placed.
                      </td>
                    </tr>
                    <tr
                      className={commit.simulatedPrice ? 'w7-book-row w7-sim' : 'w7-book-row'}
                      // `data-kind="hedge"` paints the info tint and beats
                      // `.w7-sim` on specificity, so a hedge derived from the
                      // simulated ladder deliberately drops it and keeps the
                      // warn wash instead. Simulation is contagious; the info
                      // tint is not the marker it needs.
                      data-kind={commit.simulatedPrice ? undefined : 'hedge'}
                      data-stale={moved ? 'true' : undefined}
                    >
                      <th scope="row" className="w7-book-label">
                        <span className="w7-book-indent">
                          Target ({money(commit.target)})
                          {commit.simulatedPrice ? ' (simulated price)' : ''}
                          {moved ? (
                            <Chip size="sm" tone="warn" icon="warning">
                              Recalculate — the book has moved
                            </Chip>
                          ) : null}
                        </span>
                      </th>
                      <BookCells
                        positions={commit.result.positions}
                        worstCase={commit.result.worstCase}
                        stale={false}
                      />
                    </tr>
                  </>
                ) : null}

                {/* ------------------------------------------- group C -- */}
                <SimulatedGroup
                  span={span}
                  positions={positions}
                  exchange={book.simulatedHedge.exchange}
                  local={book.simulatedHedge.local}
                />
              </>
            )}
          </tbody>
        </table>
      </ScrollRegion>
    </Card>
  )
}

/* --------------------------------------------------------- simulated rows -- */

const netAt = (rows: BookPosition[], name: string) =>
  rows.find((row) => row.name === name)?.net ?? 0

const worstOf = (rows: BookPosition[]) =>
  rows.length === 0 ? 0 : Math.min(...rows.map((row) => row.net))

/**
 * The Betfair reconciliation pair, permanently labelled.
 *
 * These rows are DISPLAY-ONLY. They are excluded from the KPI tiles, from the
 * tree arithmetic, from the hedge calculator and from CSV export — one fewer
 * route by which an invented number leaves the building.
 */
function SimulatedGroup({
  span,
  positions,
  exchange,
  local,
}: {
  span: number
  positions: BookPosition[]
  exchange: BookPosition[]
  local: BookPosition[]
}) {
  // Realign both rows onto the market's selection order. Reading Pakistan's
  // number under Sri Lanka's header is the one failure this table cannot
  // survive, and the two arrays arrive from a generator, not from the grid.
  const align = (rows: BookPosition[]) =>
    positions.map((position) => ({ name: position.name, net: netAt(rows, position.name) }))

  const difference = positions.map((position) => ({
    name: position.name,
    net: round2(netAt(exchange, position.name) - netAt(local, position.name)),
  }))

  return (
    <>
      <tr className="w7-book-group w7-sim">
        <td colSpan={span}>
          <strong>No exchange feed is connected.</strong> The rows below are generated from the
          fixture. They are not a Betfair book, they are not your hedge account, and nothing in this
          console reconciles or settles against them.
        </td>
      </tr>

      <SimRow label="Betfair book — exchange (simulated)" positions={align(exchange)} />
      <SimRow label="Betfair book — local record (simulated)" positions={align(local)} />
      {difference.some((entry) => entry.net !== 0) ? (
        <SimRow
          label="Difference, exchange − local (simulated)"
          positions={difference}
          title="In production a non-zero difference means the exchange and the platform's own record of your hedge disagree, and must be reconciled before settlement."
        />
      ) : null}
    </>
  )
}

function SimRow({
  label,
  positions,
  title,
}: {
  label: string
  positions: BookPosition[]
  title?: string
}) {
  return (
    <tr className="w7-book-row w7-sim" data-kind="sim">
      <th scope="row" className="w7-book-label" title={title}>
        <span className="w7-book-indent">{label}</span>
      </th>
      <td className="w7-book-net">
        <Money value={worstOf(positions)} signed />
      </td>
      {positions.map((entry) => (
        <td key={entry.name}>
          <Money value={entry.net} signed />
        </td>
      ))}
    </tr>
  )
}

/* ------------------------------------------------------------- skeletons -- */

/** Four rows at the real row height, so nothing jumps when the data lands. */
function BookSkeleton({ rows, selectionCount }: { rows: number; selectionCount: number }) {
  return (
    <>
      {Array.from({ length: rows }).map((_, r) => (
        <tr key={r} className="w7-book-row">
          <th scope="row" className="w7-book-label">
            <Skeleton width={160} height={14} />
          </th>
          <td className="w7-book-net">
            <Skeleton width={80} height={12} />
          </td>
          {Array.from({ length: selectionCount }).map((__, c) => (
            <td key={c}>
              <Skeleton width={80} height={12} />
            </td>
          ))}
        </tr>
      ))}
    </>
  )
}
